import React, { useEffect, useState } from 'react';
import { CircularProgress, TextField } from '@material-ui/core';
import { IconButton } from '@clef/client-library';
import { useUpdateProjectFolderMutation } from '@/serverStore/projectFolders';
import Check from '@material-ui/icons/Check';
import { useSnackbar } from 'notistack';
import { isValidProjectName } from '@/components/Utils/Validation';

interface EditFolderNameTextFieldProps {
  name: string;
  folderId: number;
  setIsEditingName: (isEditingName: boolean) => void;
}

export const EditFolderNameTextField: React.FC<EditFolderNameTextFieldProps> = props => {
  const { name, folderId, setIsEditingName } = props;
  const updateFolder = useUpdateProjectFolderMutation();
  const [folderName, setFolderName] = useState(name ?? '');
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    name && setFolderName(name);
  }, [name]);

  const handleSave = async () => {
    const trimmedName = folderName.trim();
    if (!trimmedName) {
      setIsEditingName(false);
      return;
    }
    if (trimmedName === name) {
      setIsEditingName(false);
      return;
    }
    if (!isValidProjectName(trimmedName)) {
      enqueueSnackbar(
        t('Folder name can only contain letters, numbers, dashes, underscores and spaces'),
        { variant: 'error' },
      );
      return;
    }
    try {
      await updateFolder.mutateAsync({
        id: folderId,
        name: trimmedName,
      });
      setIsEditingName(false);
    } catch (err: any) {
      const errorMessage = err?.body?.message || err?.message || t('Failed to rename folder');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  return (
    <>
      <TextField
        inputProps={{
          'aria-label': 'folder-edit-name-field',
        }}
        value={folderName}
        onClick={e => e.stopPropagation()}
        onChange={e => setFolderName(e.target.value)}
        disabled={updateFolder.isLoading}
        onKeyUp={async e => {
          if (e.key === 'Enter') {
            await handleSave();
          }
          if (e.key === 'Escape') {
            setIsEditingName(false);
          }
        }}
        onBlur={async e => {
          if (
            e.relatedTarget &&
            (e.relatedTarget as HTMLElement).id === 'folder-more-icon-button'
          ) {
            return;
          }
          await handleSave();
        }}
        autoFocus
      />
      {updateFolder.isLoading ? (
        <IconButton id="edit-folder-name-loading" disabled>
          <CircularProgress size={16} />
        </IconButton>
      ) : (
        <IconButton
          id="edit-folder-name-done"
          onClick={async e => {
            e.stopPropagation();
            await handleSave();
          }}
          color="primary"
        >
          <Check fontSize="small" />
        </IconButton>
      )}
    </>
  );
};

export default EditFolderNameTextField;
